import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  createUserWithEmailAndPassword, 
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged
} from 'firebase/auth';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { auth, db } from '../../services/firebase';
import { syncUserToHubSpot } from '../../services/hubspotService';

const AuthContext = createContext();

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }) {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userProfile, setUserProfile] = useState(null);

  // Sign up function
  async function signup(email, password) {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    
    // Create user profile in Firestore
    const userData = {
      email: email,
      createdAt: new Date(),
      isConfigured: false,
      company: null
    };
    
    // Save to Firestore
    await setDoc(doc(db, "users", userCredential.user.uid), userData);
    
    // Sync user to HubSpot for marketing
    try {
      await syncUserToHubSpot(userData);
    } catch (error) {
      console.error("Error syncing user to HubSpot:", error);
      // Non-blocking - continue even if HubSpot sync fails
    }
    
    return userCredential.user;
  }

  // Login function
  function login(email, password) {
    return signInWithEmailAndPassword(auth, email, password);
  }

  // Logout function
  function logout() {
    // Clean up any Google Calendar session storage
    if (currentUser) {
      // Clean up user-specific session storage entries
      const calendarCallbackKey = `gcal_callback_processed_${currentUser.uid}`;
      sessionStorage.removeItem(calendarCallbackKey);
      
      // Also clean up legacy key just in case
      sessionStorage.removeItem('gcal_callback_processed');
    }
    
    return signOut(auth);
  }

  // Update user profile
  async function updateUserProfile(data) {
    const userRef = doc(db, "users", currentUser.uid);
    await setDoc(userRef, data, { merge: true });
    // Update local state
    setUserProfile({ ...userProfile, ...data });
  }

  // Get user profile
  async function fetchUserProfile(uid) {
    const userRef = doc(db, "users", uid);
    const userSnap = await getDoc(userRef);
    
    if (userSnap.exists()) {
      const userData = userSnap.data();
      setUserProfile(userData);
      return userData;
    } else {
      return null;
    }
  }

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setCurrentUser(user);
      if (user) {
        await fetchUserProfile(user.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const value = {
    currentUser,
    userProfile,
    login,
    signup,
    logout,
    updateUserProfile,
    fetchUserProfile
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}